import type { CSSProperties } from "react";
import type { AIChart as AIChartSpec, AIToolCallRecord } from "../types/ai.types";
import AIChart from "./AIChart";

// ---------------------------------------------------------------------------
// ChartCards — inline charts for an assistant turn (typed chat).
//
//   • run_report   → result.chart (the report tool drew a chart of its rows)
//   • render_chart → result.action === "chart" && result.chart
//
// Same idea as RecordingCards: picked off the turn's tool results so the
// chart sits right under the answer instead of buried in tool-chip JSON.
// ---------------------------------------------------------------------------

function chartOf(t: AIToolCallRecord): AIChartSpec | null {
  const r = t.result;
  if (!r || typeof r !== "object") return null;
  const bearing = t.name === "run_report" || (t.name === "render_chart" && r.action === "chart");
  if (!bearing) return null;
  const c = r.chart;
  if (!c || typeof c !== "object" || !Array.isArray(c.categories) || !Array.isArray(c.series)) return null;
  return c as AIChartSpec;
}

export default function ChartCards({ toolCalls }: { toolCalls: AIToolCallRecord[] }) {
  const charts = toolCalls.flatMap((t) => {
    const chart = chartOf(t);
    return chart ? [{ id: t.id, chart }] : [];
  });
  if (!charts.length) return null;

  return (
    <div style={wrap}>
      {charts.map(({ id, chart }) => (
        <div key={id} style={card}>
          <AIChart chart={chart} />
        </div>
      ))}
    </div>
  );
}

// The assistant message is a shrink-to-fit flex item (alignSelf + maxWidth),
// so a percentage-width chart contributes nothing to its intrinsic width and
// would collapse under a one-line answer. The minWidth gives the message a
// floor; width:100% lets the card grow with a longer answer.
const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  margin: "6px 0",
  width: "100%",
  minWidth: 260,
};
const card: CSSProperties = {
  border: "1px solid var(--color-black-3, #e5e7eb)",
  borderRadius: 10,
  padding: "8px 10px",
  background: "var(--color-bg, #fff)",
};
